<template>
  <div class="row mb-40">
    <div class="col-xxl-10 col-lg-8 pe-xxl-8">
      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-25 position-relative">
            <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.order_tax_percent") }}</label>
            <input
              type="number"
              :value="orderTax"
              @input="onInput('orderTax', ($event.target as HTMLInputElement).value)"
              class="w-100 d-block shadow-none fs-14 bg-white rounded-1 text-title h-55 ps-3"
              placeholder="0"
            />
            <span
              class="percent-sign position-absolute rounded-1 text-center d-flex flex-column justify-content-center"
              style="right: 15px; top: 38px;"
            >
              %
            </span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-25 position-relative">
            <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.discount_amount") }}</label>
            <input
              type="number"
              :value="discount"
              @input="onInput('discount', ($event.target as HTMLInputElement).value)"
              class="w-100 d-block shadow-none fs-14 bg-white rounded-1 text-title h-55 ps-3"
              placeholder="0"
            />
            <span
              class="percent-sign position-absolute rounded-1 text-center d-flex flex-column justify-content-center fw-semibold fs-16"
              style="right: 15px; top: 38px;"
            >
              $
            </span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-25 position-relative">
            <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.shipping_cost_amount") }}</label>
            <input
              type="number"
              :value="shipping"
              @input="onInput('shipping', ($event.target as HTMLInputElement).value)"
              class="w-100 d-block shadow-none fs-14 bg-white rounded-1 text-title h-55 ps-3"
              placeholder="0"
            />
            <span
              class="percent-sign position-absolute rounded-1 text-center d-flex flex-column justify-content-center fw-semibold fs-16"
              style="right: 15px; top: 38px;"
            >
              $
            </span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-25">
            <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.status") }}</label>
            <select 
              :value="status" 
              @change="onInput('status', ($event.target as HTMLSelectElement).value)"
              class="bg-white border-0 rounded-1 fs-14 text-optional w-100 h-55 ps-3"
            >
              <option value="sent">{{ $t("purchase_page.purchase_status_sent") }}</option>
              <option value="received">{{ $t("purchase_page.purchase_status_received") }}</option>
              <option value="pending">{{ $t("purchase_page.purchase_status_pending") }}</option>
              <option value="ordered">{{ $t("purchase_page.purchase_status_ordered") }}</option>
            </select>
          </div>
        </div>
        <!-- Payment Fields -->
        <div class="col-md-6">
          <div class="form-group mb-25 position-relative">
            <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.paid_amount") }}</label>
            <input
              type="number"
              :value="paidAmount"
              @input="onInput('paidAmount', ($event.target as HTMLInputElement).value)"
              class="w-100 d-block shadow-none fs-14 bg-white rounded-1 text-title h-55 ps-3"
              placeholder="0"
            />
            <span
              class="percent-sign position-absolute rounded-1 text-center d-flex flex-column justify-content-center fw-semibold fs-16"
              style="right: 15px; top: 38px;"
            >
              $
            </span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-25">
            <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.payment_method") }}</label>
            <select 
              :value="paymentMethodId ?? ''" 
              @change="onInput('paymentMethodId', ($event.target as HTMLSelectElement).value)"
              class="bg-white border-0 rounded-1 fs-14 text-optional w-100 h-55 ps-3"
            >
              <option value="">{{ $t("purchase_page.select_method") }}</option>
              <option v-for="method in paymentMethods" :key="method.id" :value="method.id">
                {{ method.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
    <div class="col-xxl-2 col-lg-4">
      <div class="card border-0 rounded-1 w-xxl-5 pt-12 pb-12 mb-md-25 shadow-sm">
        <table class="table style-two">
          <tbody>
            <tr>
              <th scope="row" class="fs-14 text-title lh-1 ls-1 fw-normal">
                {{ $t("purchase_page.order_tax") }} :
              </th>
              <td class="fs-14 fw-semibold lh-1 text-optional text-end">
                ${{ Number(calculatedTax).toFixed(2) }}
              </td>
            </tr>
            <tr>
              <th scope="row" class="fs-14 text-title lh-1 ls-1 fw-normal">
                {{ $t("purchase_page.discount") }} :
              </th>
              <td class="fs-14 fw-semibold lh-1 text-optional text-end">
                ${{ Number(discount).toFixed(2) }}
              </td>
            </tr>
            <tr>
              <th scope="row" class="fs-14 text-title lh-1 ls-1 fw-normal">
                {{ $t("purchase_page.shipping") }} :
              </th>
              <td class="fs-14 fw-semibold lh-1 text-optional text-end">
                ${{ Number(shipping).toFixed(2) }}
              </td>
            </tr>
            <tr>
              <th scope="row" class="fs-14 text-title lh-1 ls-1 fw-semibold">
                {{ $t("purchase_page.grand_total") }} :
              </th>
              <td class="fs-14 fw-bold lh-1 text-purple text-end">${{ Number(grandTotal).toFixed(2) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="col-12">
      <div class="form-group mb-25">
        <label class="d-block fs-14 text-black mb-2">{{ $t("purchase_page.notes") }}</label>
        <textarea
          :value="notes"
          @input="onInput('notes', ($event.target as HTMLTextAreaElement).value)"
          cols="30"
          rows="5"
          :placeholder="$t('purchase_page.add_note')"
          class="d-block w-100 bg-white border-0 rounded-1 resize-none fs-14 text-title p-3"
        ></textarea>
      </div>
    </div>
    <div class="col-xl-4">
      <button
        @click="$emit('submit')"
        class="btn style-one d-inline-block transition border-0 fw-medium text-white rounded-1 fs-md-15 fs-lg-16 mb-20"
        type="button"
      >
        {{ $t("purchase_page.submit_purchase") }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";

export default defineComponent({
  name: "SubmitPurchase",
  props: {
    orderTax: { type: Number, default: 0 },
    discount: { type: Number, default: 0 },
    shipping: { type: Number, default: 0 },
    status: { type: String, default: "received" },
    notes: { type: String, default: "" },
    calculatedTax: { type: Number, default: 0 },
    grandTotal: { type: Number, default: 0 },
    paidAmount: { type: Number, default: 0 },
    paymentMethodId: { type: [Number, String, Object] as any, default: null },
    paymentMethods: { type: Array as () => any[], default: () => [] },
  },
  emits: [
    "update:orderTax",
    "update:discount",
    "update:shipping",
    "update:status",
    "update:notes",
    "update:paidAmount",
    "update:paymentMethodId",
    "submit"
  ],
  setup(props, { emit }) {
    // No need for local refs if we use computed or direct emits for v-model
    // but for immediate UI responsiveness without waiting for parent, we can use local or just emit
    
    const onInput = (field: string, value: any) => {
       if (['orderTax', 'discount', 'shipping', 'paidAmount'].includes(field)) {
         emit(`update:${field}` as any, parseFloat(value) || 0);
       } else if (field === 'paymentMethodId') {
         emit(`update:${field}` as any, value ? parseInt(value) : null);
       } else {
         emit(`update:${field}` as any, value);
       }
     };

    return {
      onInput
    };
  },
});
</script>
